import React from 'react';
import { Award, CheckCircle2, Clock, ClipboardList } from 'lucide-react';
import type { StudentQuizScore } from '../../types/quizResult';
import { cardTheme } from '../../styles/cardTheme';
import { formatWalletDate } from '../../utils/walletLabels';

interface QuizScoreCardProps {
  quiz: StudentQuizScore;
}

function isQuizSolved(quiz: StudentQuizScore): boolean {
  return quiz.submittedAt != null;
}

function getScorePercent(quiz: StudentQuizScore): number {
  if (!quiz.fullMark) return 0;
  return Math.round((quiz.score / quiz.fullMark) * 100);
}

function getGradeLabel(percent: number): { text: string; className: string } {
  if (percent >= 85) return { text: 'ممتاز', className: 'bg-green-100 text-green-800 border-green-200' };
  if (percent >= 70) return { text: 'جيد جداً', className: 'bg-gold-50 text-gold-800 border-gold-200' };
  if (percent >= 60) return { text: 'جيد', className: 'bg-amber-50 text-amber-800 border-amber-200' };
  return { text: 'يحتاج مراجعة', className: 'bg-red-50 text-red-700 border-red-200' };
}

function getRingColor(percent: number): string {
  if (percent >= 85) return 'stroke-green-600';
  if (percent >= 60) return 'stroke-gold-500';
  return 'stroke-red-500';
}

const ScoreRing: React.FC<{ percent: number }> = ({ percent }) => {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(percent, 100) / 100) * circumference;

  return (
    <div className="relative h-[5.5rem] w-[5.5rem] shrink-0">
      <svg className="h-full w-full -rotate-90" viewBox="0 0 88 88" aria-hidden>
        <circle
          cx="44"
          cy="44"
          r={radius}
          fill="none"
          className="stroke-green-100"
          strokeWidth="7"
        />
        <circle
          cx="44"
          cy="44"
          r={radius}
          fill="none"
          className={`${getRingColor(percent)} transition-all duration-500`}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-green-900 leading-none">{percent}%</span>
        <span className="mt-0.5 text-[10px] font-medium text-gray-500">النتيجة</span>
      </div>
    </div>
  );
};

const QuizScoreCard: React.FC<QuizScoreCardProps> = ({ quiz }) => {
  const solved = isQuizSolved(quiz);
  const percent = getScorePercent(quiz);
  const grade = getGradeLabel(percent);

  return (
    <article
      className={`${cardTheme.shell} ${cardTheme.shellHover} ${cardTheme.accentTop} overflow-hidden`}
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl shadow-sm ${
              solved ? cardTheme.iconBox : 'bg-gray-100 text-gray-500'
            }`}
          >
            {solved ? <Award className="h-5 w-5" /> : <ClipboardList className="h-5 w-5" />}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h2 className="font-bold text-green-900 text-sm sm:text-base leading-snug line-clamp-2 font-cairo-bold">
                {quiz.quizName}
              </h2>
              <span
                className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold ${
                  solved
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-gray-200 bg-gray-50 text-gray-600'
                }`}
              >
                {solved ? (
                  <>
                    <CheckCircle2 className="h-3 w-3" />
                    تم الحل
                  </>
                ) : (
                  <>
                    <Clock className="h-3 w-3" />
                    لم يُحل
                  </>
                )}
              </span>
            </div>

            {quiz.submittedAt && (
              <p className="mt-1.5 text-xs text-gray-500">{formatWalletDate(quiz.submittedAt)}</p>
            )}
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-green-100 bg-gradient-to-br from-green-50/80 via-white to-gold-50/40 p-4">
          {solved ? (
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-between">
              <ScoreRing percent={percent} />

              <div className="flex-1 text-center sm:text-right">
                <p className="text-xs font-medium text-gray-500">الدرجة</p>
                <p className="mt-1 text-2xl font-bold text-green-900">
                  <span className={cardTheme.price}>{quiz.score.toLocaleString('ar-EG')}</span>
                  <span className="mx-1.5 text-gray-400 font-normal">/</span>
                  <span className="text-gray-600">{quiz.fullMark.toLocaleString('ar-EG')}</span>
                </p>
                <span
                  className={`mt-2 inline-flex rounded-full border px-3 py-1 text-xs font-bold ${grade.className}`}
                >
                  {grade.text}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-2 text-center sm:flex-row sm:justify-between sm:text-right">
              <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-dashed border-gray-300 bg-white">
                <Clock className="h-7 w-7 text-gray-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">في انتظار الحل</p>
                <p className="mt-1 text-xs text-gray-500">
                  الدرجة الكاملة:{' '}
                  <span className="font-bold text-green-800">
                    {quiz.fullMark.toLocaleString('ar-EG')}
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default QuizScoreCard;
